/**
 * Matrice profils OIDC × capacités - miroir de monorg-api/server/app/abilities/role_capability_matrix.ts
 * Réf. canonique : monorga-ihm/docs/role-capability-matrix.md (copie) + monorg-api/docs/role-capability-matrix.md (source)
 * YYX_ADMIN : les capacités ORG_EDIT_* sont « oui » au niveau profil ; l’effectivité reste limitée aux directions de rattachement organigramme (API : directionAdminDirLibs, userCanEditOrganigrammeInDirection).
 * YYX_DEVELOPPEUR : ligne matrice alignée sur l’API (orga + tags + outils techniques) ; édition organigramme côté IHM comme le super-admin.
 */
import { ERole } from './e_roles'

export enum AppCapability {
  ORG_READ = 'ORG_READ',
  ADMIN_HUB = 'ADMIN_HUB',
  ORG_EDIT_DIRECTION = 'ORG_EDIT_DIRECTION',
  ORG_EDIT_DOMAINE = 'ORG_EDIT_DOMAINE',
  ORG_REORDER_DOMAINE = 'ORG_REORDER_DOMAINE',
  ORG_EDIT_AGENCE = 'ORG_EDIT_AGENCE',
  ORG_EDIT_EQUIPE = 'ORG_EDIT_EQUIPE',
  ADMIN_TAGS = 'ADMIN_TAGS',
  ADMIN_TECH_TOOLS = 'ADMIN_TECH_TOOLS',
  VIEW_ROLE_MATRIX = 'VIEW_ROLE_MATRIX',
}

const OIDC_PROFILE_SUPER_ADMIN = ERole.SUPER_ADMIN
const OIDC_PROFILE_ADMIN = ERole.ADMIN
const OIDC_PROFILE_DEVELOPER = ERole.DEVELOPER
export const OIDC_PROFILE_MANAGER = ERole.MANAGER

type CapabilityRow = Partial<Record<AppCapability, true>>

/** Ordre des clés aligné sur l’API (colonne développeur à gauche dans la page admin). */
const MATRIX_BY_PROFILE: Record<string, CapabilityRow> = {
  [OIDC_PROFILE_DEVELOPER]: {
    [AppCapability.ORG_READ]: true,
    [AppCapability.ADMIN_HUB]: true,
    [AppCapability.ORG_EDIT_DIRECTION]: true,
    [AppCapability.ORG_EDIT_DOMAINE]: true,
    [AppCapability.ORG_REORDER_DOMAINE]: true,
    [AppCapability.ORG_EDIT_AGENCE]: true,
    [AppCapability.ORG_EDIT_EQUIPE]: true,
    [AppCapability.ADMIN_TAGS]: true,
    [AppCapability.ADMIN_TECH_TOOLS]: true,
    [AppCapability.VIEW_ROLE_MATRIX]: true,
  },
  [OIDC_PROFILE_SUPER_ADMIN]: {
    [AppCapability.ORG_READ]: true,
    [AppCapability.ADMIN_HUB]: true,
    [AppCapability.ORG_EDIT_DIRECTION]: true,
    [AppCapability.ORG_EDIT_DOMAINE]: true,
    [AppCapability.ORG_REORDER_DOMAINE]: true,
    [AppCapability.ORG_EDIT_AGENCE]: true,
    [AppCapability.ORG_EDIT_EQUIPE]: true,
    [AppCapability.ADMIN_TAGS]: true,
    [AppCapability.VIEW_ROLE_MATRIX]: true,
  },
  [OIDC_PROFILE_ADMIN]: {
    [AppCapability.ORG_READ]: true,
    [AppCapability.ADMIN_HUB]: true,
    [AppCapability.ORG_EDIT_DIRECTION]: true,
    [AppCapability.ORG_EDIT_DOMAINE]: true,
    [AppCapability.ORG_REORDER_DOMAINE]: true,
    [AppCapability.ORG_EDIT_AGENCE]: true,
    [AppCapability.ORG_EDIT_EQUIPE]: true,
    [AppCapability.VIEW_ROLE_MATRIX]: true,
  },
  [OIDC_PROFILE_MANAGER]: {
    [AppCapability.ORG_READ]: true,
  },
}

export function userHasCapability(roles: string[], capability: AppCapability): boolean {
  for (const profile of roles) {
    const row = MATRIX_BY_PROFILE[profile]
    if (row?.[capability]) {
      return true
    }
  }
  return capability === AppCapability.ORG_READ && roles.length === 0
}

/** Tags : SuperAdmin via matrice ; admin legacy pendant migration (aligné API userCanAdminTags). */
export function canAdminTagsRoleSet(roles: string[]): boolean {
  return (
    userHasCapability(roles, AppCapability.ADMIN_TAGS) || roles.includes(OIDC_PROFILE_ADMIN)
  )
}

export function canAccessAdminHubRoleSet(roles: string[]): boolean {
  return userHasCapability(roles, AppCapability.ADMIN_HUB)
}

export function canViewRoleMatrixPageRoleSet(roles: string[]): boolean {
  return userHasCapability(roles, AppCapability.VIEW_ROLE_MATRIX)
}

export function canUseTechToolsRoleSet(roles: string[]): boolean {
  return userHasCapability(roles, AppCapability.ADMIN_TECH_TOOLS)
}
